import { z } from 'zod';
import { pageMetaSchema } from './common.js';
import { telemetryScoreOpsResponseSchema } from './telemetry-score.js';
import { comparableFmvSchema } from './comparable-fmv.js';

/**
 * Asset DTOs.
 *
 * Shape mirrors db/schema/300_views_asset.sql:
 *   v_asset_list             → assetListItemSchema
 *   v_asset_detail           → assetDetailSchema (one row)
 *   v_asset_snapshot_history → assetSnapshotHistoryItemSchema (paged inline)
 *   v_asset_telemetry_recent → assetTelemetryRowSchema (≤ 30 days)
 *
 * Numerics from Postgres arrive as strings via node-postgres; z.coerce.number
 * normalizes them at the API boundary so the UI sees real numbers.
 */

// Enum echoes (kept loose — the engine may grow new condition bands etc.)
export const conditionBandSchema = z.enum([
  'new',
  'like_new',
  'normal_enterprise_wear',
  'high_wear',
  'unknown',
  // SIL-1534: for-parts / not-working / as-is. Deliberately NOT paired with a
  // condition_multiplier entry anywhere (db/schema/090_fmv_config.sql,
  // fmv-config/formula.config.ts's conditionMultipliers) - a defective asset
  // fails CLOSED (resolveMultipliersBootstrap throws no_condition_multiplier,
  // mapped to 422) rather than getting an invented condition-multiplied FMV.
  'defective',
]);
export const marketabilityTierSchema = z.enum(['A', 'B', 'C']);
// Manually-settable lifecycle stages (direct-set edit gate). Excludes 'sold'
// — that state is set only by the ownership-transfer flow (525) and is a
// terminal lock, never a picker option.
export const assetLifecycleStateSchema = z.enum([
  'active',
  'idle',
  'in_transit',
  'decommissioned',
  'disposed',
  'quarantined',
]);
// Bootstrap-only baseline source selection (RUF-374) — narrower than the full
// baseline_price_source lookup table since bootstrap only supports the two
// sources an analyst can pick from the ops UI with no prior snapshot.
export const bootstrapBaselineSourceSchema = z.enum(['sku_mcmp', 'manual']);
export const skuMatchStatusSchema = z.enum(['verified', 'inferred', 'unmatched']);

// Shared kebab-case enums for filter URL params (UI ↔ API contract for
// list filtering). Mirrors enum names but coerced to allow free-text 'q'.
export const assetListFilterSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  seller_id: z.string().uuid().optional(),
  lifecycle_state: z.string().optional(),
  condition_band: conditionBandSchema.optional(),
  asset_class: z.string().optional(),
  sku_id: z.string().uuid().optional(),
  q: z.string().min(1).optional(),
  allows_listing: z.enum(['true', 'false']).optional(),
  is_test: z.enum(['true', 'false']).optional(),
  sku_match_status: skuMatchStatusSchema.optional(),
});
export type AssetListFilter = z.infer<typeof assetListFilterSchema>;

// ---------------------------------------------------------------------------
// List item — one row of v_asset_list
// ---------------------------------------------------------------------------
export const assetListItemSchema = z.object({
  assetId: z.string().uuid(),
  sellerOrgId: z.string().uuid(),
  sellerLegalName: z.string(),
  sellerShortName: z.string().nullable(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  skuId: z.string().uuid().nullable(),
  verifiedSku: z.string().nullable(),
  skuModelName: z.string().nullable(),
  manufacturerId: z.string().nullable(),
  manufacturerName: z.string().nullable(),
  skuMatchStatus: skuMatchStatusSchema,
  sellerSkuRaw: z.string().nullable(),
  sellerManufacturerRaw: z.string().nullable(),
  sellerModelRaw: z.string().nullable(),
  lifecycleStateId: z.string(),
  lifecycleStateName: z.string(),
  conditionBand: conditionBandSchema.nullable(),
  dataCenterId: z.string().uuid().nullable(),
  dataCenterCode: z.string().nullable(),
  regionId: z.string().nullable(),
  serialNumber: z.string().nullable(),
  latestPricingSnapshotId: z.string().uuid().nullable(),
  latestPricedAt: z.string().nullable(),
  latestFmvMedUsd: z.coerce.number().nullable(),
  // SIL-1648: units on this row (1 = a serialized unit, > 1 = a stock line).
  // latestFmvMedUsd stays PER UNIT; lineValueUsd = latestFmvMedUsd x quantity
  // (v_asset_unit_value), null when unpriced.
  quantity: z.number().int().positive(),
  lineValueUsd: z.coerce.number().nullable(),
  latestNetRecoveryUsd: z.coerce.number().nullable(),
  latestMarketabilityTier: marketabilityTierSchema.nullable(),
  latestConfidenceScore: z.coerce.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Customer/seller-declared inventory tag (686) — second key in the ERP
  // register import match waterfall (serial → asset_tag → po → triage).
  assetTag: z.string().nullable(),
});
export type AssetListItem = z.infer<typeof assetListItemSchema>;

export const assetListResponseSchema = z.object({
  data: z.array(assetListItemSchema),
  meta: pageMetaSchema,
});
export type AssetListResponse = z.infer<typeof assetListResponseSchema>;

// ---------------------------------------------------------------------------
// Detail — one row of v_asset_detail, with snapshot history + telemetry
// folded into the same payload for one round-trip to render the page.
// ---------------------------------------------------------------------------
export const assetDetailRowSchema = z.object({
  assetId: z.string().uuid(),
  sellerOrgId: z.string().uuid(),
  sellerLegalName: z.string(),
  sellerShortName: z.string().nullable(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  skuId: z.string().uuid().nullable(),
  verifiedSku: z.string().nullable(),
  skuModelName: z.string().nullable(),
  manufacturerId: z.string().nullable(),
  manufacturerName: z.string().nullable(),
  skuGeneration: z.string().nullable(),
  skuSubClass: z.string().nullable(),
  skuLaunchDate: z.string().nullable(),
  skuLaunchPriceUsd: z.coerce.number().nullable(),
  skuMatchStatus: skuMatchStatusSchema,
  skuMatchedAt: z.string().nullable(),
  skuMatchReason: z.string().nullable(),
  sellerSkuRaw: z.string().nullable(),
  sellerManufacturerRaw: z.string().nullable(),
  sellerModelRaw: z.string().nullable(),
  serialNumber: z.string().nullable(),
  observedSerial: z.string().nullable(),
  lifecycleStateId: z.string(),
  lifecycleStateName: z.string(),
  conditionBand: conditionBandSchema.nullable(),
  conditionSourceId: z.string().nullable(),
  conditionAssessedAt: z.string().nullable(),
  dataCenterId: z.string().uuid().nullable(),
  dataCenterCode: z.string().nullable(),
  dataCenterName: z.string().nullable(),
  regionId: z.string().nullable(),
  classAttributes: z.record(z.unknown()).nullable(),
  schemaVersion: z.number(),
  activeNodeId: z.string().uuid().nullable(),
  activeNodeOpenedAt: z.string().nullable(),
  activeSellerNodeId: z.string().nullable(),
  latestPricingSnapshotId: z.string().uuid().nullable(),
  latestPricedAt: z.string().nullable(),
  latestFmvLowUsd: z.coerce.number().nullable(),
  latestFmvMedUsd: z.coerce.number().nullable(),
  latestFmvHighUsd: z.coerce.number().nullable(),
  latestNetRecoveryUsd: z.coerce.number().nullable(),
  latestConfidenceScore: z.coerce.number().nullable(),
  // SIL-1648: see assetListItemSchema. Per-unit FMV above, line total here.
  quantity: z.number().int().positive(),
  lineValueUsd: z.coerce.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  oemSpec: z.object({
    source: z.enum(['oem_scrape', 'catalog_fallback', 'none']),
    scrapedAt: z.string().nullable(),
    vramGb: z.coerce.number().nullable(),
    tdpWatts: z.coerce.number().nullable(),
    architecture: z.string().nullable(),
    interconnectType: z.string().nullable(),
    formFactor: z.string().nullable(),
    maxGpuCount: z.coerce.number().nullable(),
    pcieGen: z.coerce.number().nullable(),
    powerSupplyWatts: z.coerce.number().nullable(),
  }).nullable(),
  // Customer/seller-declared inventory tag (686) — second key in the ERP
  // register import match waterfall (serial → asset_tag → po → triage).
  assetTag: z.string().nullable(),
});
export type AssetDetailRow = z.infer<typeof assetDetailRowSchema>;

export const assetSnapshotHistoryItemSchema = z.object({
  snapshotId: z.string().uuid(),
  assetId: z.string().uuid(),
  pricedAt: z.string(),
  fmvLowUsd: z.coerce.number(),
  fmvMedUsd: z.coerce.number(),
  fmvHighUsd: z.coerce.number(),
  netRecoveryUsd: z.coerce.number(),
  marketabilityTier: marketabilityTierSchema,
  confidenceScore: z.coerce.number(),
  modelVersionId: z.string().uuid(),
  modelVersion: z.string(),
  triggerId: z.string(),
  triggerName: z.string(),
  // SIL-924: value-basis disclosure. baselinePriceSource*/SampleSize are
  // verbatim captured inputs off fmv_pricing_snapshot (already existed
  // pre-SIL-924; simply not surfaced on this DTO before). compRecency*/
  // staleDataException are LIVE signals computed at read time from the
  // asset's current SKU's freshest qualifying market_comp row (SIL-923's
  // fetchSourceRecencyCompAge) — they describe how fresh the comp data
  // behind a 'sku_mcmp' baseline is TODAY, not at the historical priced_at
  // of each row, since no per-snapshot comp age is persisted. Only
  // meaningful when baselinePriceSourceId === 'sku_mcmp'; null/false for
  // 'manual' and 'oem_list' baselines, which have no comp-recency concept.
  baselinePriceSourceId: z.string(),
  baselinePriceSourceName: z.string(),
  baselinePriceSampleSize: z.number().nullable(),
  compNewestObservedAt: z.string().nullable(),
  compRecencyDays: z.coerce.number().nullable(),
  staleDataException: z.boolean(),
  // SIL-1828 (R2): server-derived from baselinePriceSourceId = 'virtual_comp'
  // (isDirectionalBaseline in fmv-resolution.ts). UIs read this; never re-derive.
  isDirectionalBaseline: z.boolean().default(false),
});
export type AssetSnapshotHistoryItem = z.infer<typeof assetSnapshotHistoryItemSchema>;

export const assetTelemetryRowSchema = z.object({
  assetId: z.string().uuid().nullable(),
  nodeId: z.string().uuid(),
  day: z.string(),
  sampleCount: z.number(),
  maxGpuTempC: z.coerce.number().nullable(),
  avgGpuTempC: z.coerce.number().nullable(),
  maxCpuTempC: z.coerce.number().nullable(),
  avgCpuTempC: z.coerce.number().nullable(),
  avgPowerDrawWatts: z.coerce.number().nullable(),
  maxPowerDrawWatts: z.coerce.number().nullable(),
  avgGpuUtilizationPct: z.coerce.number().nullable(),
  maxGpuUtilizationPct: z.coerce.number().nullable(),
  avgGpuMemoryUsedMb: z.coerce.number().nullable(),
  maxGpuMemoryUsedMb: z.coerce.number().nullable(),
  avgGpuCoreClockMhz: z.coerce.number().nullable(),
  maxGpuCoreClockMhz: z.coerce.number().nullable(),
  avgCpuUtilizationPct: z.coerce.number().nullable(),
  maxCpuUtilizationPct: z.coerce.number().nullable(),
  avgNetworkInGbps: z.coerce.number().nullable(),
  avgNetworkOutGbps: z.coerce.number().nullable(),
  totalEccErrors: z.coerce.number().nullable(),
  anyXidNonzero: z.boolean().nullable(),
  maxXidErrorCode: z.coerce.number().nullable(),
  derivedConditionBand: conditionBandSchema.nullable(),
});
export type AssetTelemetryRow = z.infer<typeof assetTelemetryRowSchema>;

export const assetDetailResponseSchema = z.object({
  detail: assetDetailRowSchema,
  snapshotHistory: z.array(assetSnapshotHistoryItemSchema),
  telemetryRecent: z.array(assetTelemetryRowSchema),
  // RUF-513: latest telemetry health-score snapshot for this asset (ops full
  // detail), or null if the asset has never been scored.
  latestTelemetryScore: telemetryScoreOpsResponseSchema.nullable().optional(),
  // SIL-1155: informational comparable-FMV range (gaming_pc with a usable
  // estimate). OMITTED - never null - when absent or suppressed.
  comparableFmv: comparableFmvSchema.optional(),
});
export type AssetDetailResponse = z.infer<typeof assetDetailResponseSchema>;

// ---------------------------------------------------------------------------
// Telemetry aggregate overrides — RUF-459 (RUF-381 parent brief).
//
// Raw 30-day telemetry_daily_agg roll-up shape the ops FMV tester can
// prefill from an asset's real telemetry and then edit field-by-field to
// run a what-if. Superset of the fields
// `resolveTelemetryMultiplierForAsset`'s TelemetryHealthAggregates and the
// condition-deriver's TelemetryWindow each need — the preview route builds
// both from this one shape so overriding e.g. `totalEccErrors` moves the
// telemetry multiplier AND the derived condition band together.
// ---------------------------------------------------------------------------
export const telemetryAggregateOverrideSchema = z.object({
  sampleCount: z.number().int().min(0),
  daysSinceLastSample: z.number().int().min(0),
  totalEccErrors: z.number().min(0),
  eccExposureDays: z.number().min(0).optional(),
  maxGpuTempC: z.number().nullable(),
  avgGpuTempC: z.number().nullable(),
  xidFaultDayCount: z.number().int().min(0),
  anyXidNonzero: z.boolean(),
  avgGpuUtilizationPct: z.number().nullable(),
  totalThrottleEvents: z.number().min(0),
  anyCritical: z.boolean().optional().default(false),
});
export type TelemetryAggregateOverride = z.infer<typeof telemetryAggregateOverrideSchema>;

// ---------------------------------------------------------------------------
// Candidate formula-config overrides — RUF-441.
//
// Lets the ops FMV tester preview an *uncommitted* formula change ("what would
// this do") before a formula PR is merged. Each field, when present, replaces
// that factor for the asset's OWN asset class in the committed
// FmvFormulaConfig; the server rebuilds a candidate config (re-hashing to a new
// version) and resolves the preview against it. Scoped to the asset's class —
// the tester edits one asset at a time.
// ---------------------------------------------------------------------------
export const fmvFormulaOverrideSchema = z.object({
  /** Replaces config.ageCurves[class] — the full bracket list for the class. */
  ageCurve: z
    .array(
      z.object({
        ageMonthsMin: z.number().int().min(0),
        ageMonthsMax: z.number().int().min(1),
        multiplier: z.number().min(0),
      }),
    )
    .optional(),
  /** Merged into config.conditionMultipliers[class] per band. */
  conditionMultipliers: z
    .record(conditionBandSchema, z.object({ low: z.number(), default: z.number(), high: z.number() }))
    .optional(),
  /** Replaces the class-level marketability default {tier, factor}. */
  marketability: z.object({ tier: marketabilityTierSchema, factor: z.number().min(0) }).optional(),
  /** Replaces config.friction[class] — the full rule list for the class. */
  friction: z
    .array(
      z.object({
        componentId: z.string().min(1),
        regionId: z.string().nullable(),
        perUnitUsd: z.number().min(0),
        fixedUsd: z.number().min(0),
      }),
    )
    .optional(),
});
export type FmvFormulaOverride = z.infer<typeof fmvFormulaOverrideSchema>;

// ---------------------------------------------------------------------------
// FMV preview — sandbox what-if compute (POST /api/assets/:assetId/fmv-preview)
// ---------------------------------------------------------------------------
export const fmvPreviewRequestSchema = z
  .object({
    conditionBand: conditionBandSchema.optional(),
    baselinePriceUsd: z.number().positive().optional(),
    ageMonths: z.number().int().min(0).optional(),
    marketabilityTier: marketabilityTierSchema.optional(),
    // Bootstrap-only (RUF-374): selects how to resolve a baseline price when
    // the asset has no prior snapshot. Ignored on the normal carry-forward
    // path (asset already has a snapshot).
    baselinePriceSourceId: bootstrapBaselineSourceSchema.optional(),
    manualPriceUsd: z.number().positive().optional(),
    // RUF-459: when present, the telemetry multiplier + derived condition
    // band are computed from these hypothetical aggregates instead of the
    // asset's stored 30-day telemetry_daily_agg roll-up.
    telemetryOverrides: telemetryAggregateOverrideSchema.optional(),
    // RUF-441: candidate formula-config overrides for the asset's class.
    formulaOverrides: fmvFormulaOverrideSchema.optional(),
  })
  .refine((v) => v.baselinePriceSourceId !== 'manual' || v.manualPriceUsd !== undefined, {
    message: 'manualPriceUsd is required when baselinePriceSourceId is "manual"',
    path: ['manualPriceUsd'],
  });
export type FmvPreviewRequest = z.infer<typeof fmvPreviewRequestSchema>;

const fmvPreviewSliceSchema = z.object({
  fmvLowUsd: z.number(),
  fmvMedUsd: z.number(),
  fmvHighUsd: z.number(),
  netRecoveryUsd: z.number(),
  itadBaselineUsd: z.number().nullable(),
  inputs: z.object({
    baselinePriceUsd: z.number(),
    ageMonths: z.number().nullable(),
    conditionBand: conditionBandSchema.nullable(),
    marketabilityTier: marketabilityTierSchema.nullable(),
    ageMultiplier: z.number(),
    configMultiplier: z.number(),
    conditionMultiplierValue: z.number(),
    marketabilityFactor: z.number(),
    // RUF-459: telemetry multiplier applied to this slice, and (when
    // telemetry — real or overridden — was present/non-neutral) the
    // condition band the same telemetry aggregates would derive via the
    // condition-deriver thresholds. Null when telemetry is absent/neutral.
    telemetryMultiplier: z.number(),
    telemetryConditionBand: conditionBandSchema.nullable(),
  }),
});

export const fmvPreviewResponseSchema = z.object({
  // null for a bootstrap preview (RUF-374) — there is no prior snapshot to
  // show as "current" when the asset has never been priced.
  current: fmvPreviewSliceSchema.nullable(),
  preview: fmvPreviewSliceSchema,
  // RUF-459: the asset's real trailing-30-day telemetry aggregates, for the
  // ops FMV tester to prefill its telemetry override section. Null when the
  // asset has no active node or no telemetry_daily_agg rows in the window
  // (pure hypothesis mode — the section stays empty but editable).
  assetTelemetryAggregates: telemetryAggregateOverrideSchema.nullable(),
  // RUF-441: the committed formula-config version, and the version the preview
  // slice was actually computed under. Equal when no formulaOverrides were
  // supplied; differ when previewing a candidate formula change.
  committedFormulaConfigVersion: z.string(),
  previewFormulaConfigVersion: z.string(),
});
export type FmvPreviewResponse = z.infer<typeof fmvPreviewResponseSchema>;

// ---------------------------------------------------------------------------
// Asset annotation — freeform notes on assets (RUF-ANNOT-1)
// ---------------------------------------------------------------------------
export const createAnnotationRequestSchema = z.object({
  body: z.string().min(1).max(4000),
});
export type CreateAnnotationRequest = z.infer<typeof createAnnotationRequestSchema>;

export const assetAnnotationSchema = z.object({
  id: z.string().uuid(),
  assetId: z.string().uuid(),
  authorRole: z.string(),
  body: z.string(),
  createdAt: z.string(),
});
export type AssetAnnotation = z.infer<typeof assetAnnotationSchema>;

export const assetAnnotationsResponseSchema = z.object({
  data: z.array(assetAnnotationSchema),
});
export type AssetAnnotationsResponse = z.infer<typeof assetAnnotationsResponseSchema>;

// ---------------------------------------------------------------------------
// Recompute snapshot — POST /api/assets/:assetId/snapshots (RUF-RECOMPUTE-1)
// Same overrides as fmv-preview + optional notes; persists to DB.
// ---------------------------------------------------------------------------
export const recomputeSnapshotRequestSchema = z
  .object({
    conditionBand: conditionBandSchema.optional(),
    baselinePriceUsd: z.number().positive().optional(),
    ageMonths: z.number().int().min(0).optional(),
    marketabilityTier: marketabilityTierSchema.optional(),
    notes: z.string().max(4000).optional(),
    // Bootstrap-only (RUF-374): selects how to resolve a baseline price when
    // the asset has no prior snapshot (first-ever recompute). Ignored on the
    // normal carry-forward path (asset already has a snapshot).
    baselinePriceSourceId: bootstrapBaselineSourceSchema.optional(),
    manualPriceUsd: z.number().positive().optional(),
  })
  .refine((v) => v.baselinePriceSourceId !== 'manual' || v.manualPriceUsd !== undefined, {
    message: 'manualPriceUsd is required when baselinePriceSourceId is "manual"',
    path: ['manualPriceUsd'],
  });
export type RecomputeSnapshotRequest = z.infer<typeof recomputeSnapshotRequestSchema>;

export const recomputeSnapshotResponseSchema = z.object({
  snapshotId: z.string().uuid(),
  pricedAt: z.string(),
  fmvLowUsd: z.coerce.number(),
  fmvMedUsd: z.coerce.number(),
  fmvHighUsd: z.coerce.number(),
  netRecoveryUsd: z.coerce.number(),
  itadBaselineUsd: z.coerce.number().nullable(),
});
export type RecomputeSnapshotResponse = z.infer<typeof recomputeSnapshotResponseSchema>;

// ---------------------------------------------------------------------------
// CSV export filter — same fields as list filter, no cursor/limit
// ---------------------------------------------------------------------------
export const assetCsvFilterSchema = z.object({
  seller_id: z.string().uuid().optional(),
  lifecycle_state: z.string().optional(),
  condition_band: conditionBandSchema.optional(),
  asset_class: z.string().optional(),
  sku_id: z.string().uuid().optional(),
  q: z.string().min(1).optional(),
});
export type AssetCsvFilter = z.infer<typeof assetCsvFilterSchema>;

// ---------------------------------------------------------------------------
// Asset creation — POST /api/assets (manual entry)
// ---------------------------------------------------------------------------
export const assetCreateSchema = z.object({
  sellerOrgId: z.string().uuid(),
  assetClassId: z.string().min(1),
  lifecycleStateId: z.string().min(1),
  conditionBand: conditionBandSchema.optional(),
  serialNumber: z.string().nullable().optional(),
  skuId: z.string().uuid().nullable().optional(),
  assetTag: z.string().nullable().optional(),
});
export type AssetCreate = z.infer<typeof assetCreateSchema>;

// ---------------------------------------------------------------------------
// Asset tag edit — PATCH /api/assets/:assetId/asset-tag (SIL-262)
// Empty string normalizes to null (clears the tag) — mirrors the CSV/manual
// "blank means unset" convention used elsewhere in asset editing.
// ---------------------------------------------------------------------------
export const assetTagUpdateRequestSchema = z.object({
  assetTag: z
    .string()
    .max(128)
    .nullable()
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim())),
});
export type AssetTagUpdateRequest = z.infer<typeof assetTagUpdateRequestSchema>;
